<template>
  <div class="age-calculator">
    <div class="age-calculator__header">
      <h2 class="age-calculator__title">Year Group / Grade Calculator</h2>
    </div>

    <form class="age-calculator__form" @submit.prevent>
      <div class="form-field">
        <label for="academic-year-select" class="form-field__label"> Academic Year </label>
        <el-select
          id="academic-year-select"
          v-model="selectedAcademicYear"
          placeholder="Select Academic Year"
          class="form-field__input"
          :class="{ 'has-error': shouldShowAcademicYearError }"
        >
          <el-option v-for="option in academicYearOptions" :key="option.value" :label="option.label" :value="option.value" />
        </el-select>
        <span v-if="shouldShowAcademicYearError" class="form-field__error"> Please select an academic year </span>
      </div>

      <div class="form-field">
        <label for="commencement-date" class="form-field__label"> Intended Commencement Date </label>
        <el-date-picker
          id="commencement-date"
          v-model="commencementDate"
          type="date"
          placeholder="Select Intended Commencement Date"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          class="form-field__input"
          :class="{ 'has-error': shouldShowCommencementError }"
        />
        <span v-if="shouldShowCommencementError" class="form-field__error"> Please select a commencement date </span>
      </div>

      <div class="form-field">
        <label for="child-dob" class="form-field__label"> Child's Date of Birth </label>
        <el-date-picker
          id="child-dob"
          v-model="childDOB"
          type="date"
          placeholder="Select Child's Date of Birth"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          class="form-field__input"
          :class="{ 'has-error': shouldShowDOBError }"
          :picker-options="childDOBPickerOptions"
        />
        <span v-if="shouldShowDOBError" class="form-field__error"> Please select the child's date of birth </span>
      </div>
    </form>

    <div v-if="shouldShowResult" class="result-section" role="region" aria-live="polite">
      <div class="result-section__label">Recommended Grade/Year:</div>
      <div v-if="gradeYear" class="result-section__value">
        {{ gradeYear }}
      </div>
      <div v-else class="result-section__error">Unable to determine grade for the given inputs</div>
    </div>
  </div>
</template>

<script>
import { getGradeForJanDecIntake, getGradeForAugJunIntake } from '../functions/ageCalculator'

const ACADEMIC_YEAR_TYPES = {
  JAN_DEC: 'jan-dec',
  AUG_JUN: 'aug-jun',
}

/**
 * AgeCalculator Component
 *
 * Calculates the appropriate grade/year for a student based on their date of birth,
 * intended commencement date, and academic year (Jan-Dec or Aug-Jun).
 *
 * Features:
 * - Input validation with error messages
 * - Date picker restrictions for reasonable birth dates
 * - Real-time grade calculation
 * - Responsive design
 * - Accessibility support with proper labels and ARIA attributes
 */
export default {
  name: 'AgeCalculator',
  data() {
    return {
      selectedAcademicYear: null,
      commencementDate: null,
      childDOB: null,
      touchedFields: {
        academicYear: false,
        commencementDate: false,
        childDOB: false,
      },
      academicYearOptions: [
        { value: ACADEMIC_YEAR_TYPES.JAN_DEC, label: 'Jan - Dec' },
        { value: ACADEMIC_YEAR_TYPES.AUG_JUN, label: 'Aug - Jun' },
      ],
    }
  },
  computed: {
    childDOBPickerOptions() {
      const today = new Date()
      const maxDate = new Date()
      const minDate = new Date()
      minDate.setFullYear(today.getFullYear() - 18)
      maxDate.setFullYear(today.getFullYear() + 1)

      return {
        disabledDate(time) {
          return time.getTime() > maxDate.getTime() || time.getTime() < minDate.getTime()
        },
      }
    },

    gradeYear() {
      if (!this.isFormValid) return null

      try {
        switch (this.selectedAcademicYear) {
          case ACADEMIC_YEAR_TYPES.JAN_DEC:
            return getGradeForJanDecIntake(this.childDOB, this.commencementDate)
          case ACADEMIC_YEAR_TYPES.AUG_JUN:
            return getGradeForAugJunIntake(this.childDOB, this.commencementDate)
          default:
            return null
        }
      } catch (error) {
        console.error('Error calculating grade:', error)
        return null
      }
    },

    isFormValid() {
      return this.childDOB && this.commencementDate && this.selectedAcademicYear
    },

    shouldShowResult() {
      return this.isFormValid
    },

    shouldShowAcademicYearError() {
      return this.touchedFields.academicYear && !this.selectedAcademicYear
    },

    shouldShowCommencementError() {
      return this.touchedFields.commencementDate && !this.commencementDate
    },

    shouldShowDOBError() {
      return this.touchedFields.childDOB && !this.childDOB
    },
  },
  watch: {
    selectedAcademicYear() {
      this.touchedFields.academicYear = true
    },
    commencementDate() {
      this.touchedFields.commencementDate = true
    },
    childDOB() {
      this.touchedFields.childDOB = true
    },
  },
}
</script>

<style lang="scss" scoped>
.age-calculator {
  max-width: 500px;
  margin: 2rem auto;
  padding: 2rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &__header {
    text-align: center;
    margin-bottom: 2rem;
  }

  &__title {
    color: #333;
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &__label {
    font-weight: 500;
    color: #333;
    font-size: 0.9rem;
  }

  &__input {
    width: 100%;

    &.has-error {
      border-color: #f56565 !important;
    }

    :deep(.el-input__inner) {
      color: #333;
      background: #fff;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      padding: 0.75rem 1rem;
      font-size: 0.9rem;
      transition: border-color 0.2s ease;

      &:focus {
        border-color: #5f71e8;
        box-shadow: 0 0 0 3px rgba(95, 113, 232, 0.1);
      }

      &::placeholder {
        color: #9ca3af !important;
      }
    }

    &:has(.el-input__prefix):deep(.el-input__inner) {
      padding-left: 2rem;
    }

    :deep(.el-date-editor) {
      width: 100%;
    }
  }

  &__error {
    color: #f56565;
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }
}

.result-section {
  margin-top: 2rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #e2e8f0;

  &__label {
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 0.75rem;
    font-size: 1rem;
  }

  &__value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #5f71e8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__error {
    color: #f56565;
    font-size: 1rem;
    font-style: italic;
  }
}

@media (max-width: 768px) {
  .age-calculator {
    margin: 1rem;
    padding: 1.5rem;
  }
}
</style>
